import { useState, useEffect, useReducer, useContext } from 'react';
import { toast } from 'react-toastify';

import {
  Avatar,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  useTheme,
  Box,
} from '@mui/material';

import {
  AccountCircle,
  Search,
  DeleteOutline,
  Edit,
} from '@mui/icons-material';

import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import UserModal from '../../components/UserModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import UserStatusIcon from '../../components/UserModal/statusIcon';
import ForbiddenPage from '../../components/ForbiddenPage';

import whatsappIcon from '../../assets/nopicture.png';
import api from '../../services/api';
import { i18n } from '../../translate/i18n';
import toastError from '../../toasts/toastError';
import { getBackendUrl } from '../../config';
import { AuthContext } from '../../context/Auth/AuthContext';
import { getStyled } from './styles';

const backendUrl = getBackendUrl();

const reducer = (state, action) => {
  if (action.type === 'LOAD_USERS') {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === 'UPDATE_USERS') {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === 'DELETE_USER') {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === 'RESET') {
    return [];
  }
};

const Users = () => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState('');
  const [users, dispatch] = useReducer(reducer, []);
  const { user: loggedInUser, socket } = useContext(AuthContext);
  const { profileImage } = loggedInUser;

  useEffect(() => {
    dispatch({ type: 'RESET' });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const fetchUsers = async () => {
      try {
        const { data } = await api.get('/users/', {
          params: { searchParam, pageNumber },
        });
        dispatch({ type: 'LOAD_USERS', payload: data.users });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };
    fetchUsers();
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (loggedInUser) {
      const companyId = loggedInUser.companyId;
      const onCompanyUser = (data) => {
        if (data.action === 'update' || data.action === 'create') {
          dispatch({ type: 'UPDATE_USERS', payload: data.user });
        }
        if (data.action === 'delete') {
          dispatch({ type: 'DELETE_USER', payload: +data.userId });
        }
      };
      socket.on(`company-${companyId}-user`, onCompanyUser);
      return () => {
        socket.off(`company-${companyId}-user`, onCompanyUser);
      };
    }
  }, [socket, loggedInUser]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toastSuccess(i18n.t('users.toasts.deleted'));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam('');
    setPageNumber(1);
  };

  const loadMore = () => {
    setLoadingMore(true);
    setPageNumber((prevPage) => prevPage + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const renderProfileImage = (user) => {
    if (user.id === loggedInUser.id) {
      return (
        <Avatar
          src={`${backendUrl}/public/company${user.companyId}/user/${
            profileImage ? profileImage : whatsappIcon
          }`}
          alt={user.name}
          sx={classes.userAvatar}
        />
      );
    }
    if (user.id !== loggedInUser.id) {
      return (
        <Avatar
          src={
            user.profileImage
              ? `${backendUrl}/public/company${user.companyId}/user/${user.profileImage}`
              : whatsappIcon
          }
          alt={user.name}
          sx={classes.userAvatar}
        />
      );
    }
    return <AccountCircle />;
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t('users.confirmationModal.deleteTitle')} ${
            deletingUser.name
          }?`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t('users.confirmationModal.deleteMessage')}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby='form-dialog-title'
        userId={selectedUser && selectedUser.id}
      />
      {loggedInUser.profile === 'user' ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Title>
              {i18n.t('users.title')} ({users.length})
            </Title>
            <MainHeaderButtonsWrapper>
              <TextField
                sx={classes.searchField}
                placeholder={i18n.t('contacts.searchPlaceholder')}
                type='search'
                value={searchParam}
                onChange={handleSearch}
                variant='standard'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search sx={{ color: 'gray' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant='outlined'
                sx={{
                  ...theme.custom.editButton,
                  marginLeft: theme.spacing(1),
                  marginBottom: theme.spacing(1),
                }}
                onClick={handleOpenUserModal}
              >
                {i18n.t('users.buttons.add')}
              </Button>
            </MainHeaderButtonsWrapper>
          </MainHeader>
          <Paper
            sx={classes.mainPaper}
            variant='outlined'
            onScroll={handleScroll}
          >
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell align='center'>
                    {i18n.t('users.table.ID')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('users.table.status')}
                  </TableCell>
                  <TableCell align='center'>Avatar</TableCell>
                  <TableCell align='center'>
                    {i18n.t('users.table.name')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('users.table.email')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('users.table.profile')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('users.table.startWork')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('users.table.endWork')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('users.table.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell align='center'>{user.id}</TableCell>
                      <TableCell align='center'>
                        <UserStatusIcon user={user} />
                      </TableCell>
                      <TableCell align='center'>
                        <div sx={classes.avatarDiv}>
                          {renderProfileImage(user)}
                        </div>
                      </TableCell>
                      <TableCell align='center'>{user.name}</TableCell>
                      <TableCell align='center'>{user.email}</TableCell>
                      <TableCell align='center'>{user.profile}</TableCell>
                      <TableCell align='center'>{user.startWork}</TableCell>
                      <TableCell align='center'>{user.endWork}</TableCell>
                      <TableCell align='center'>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          <IconButton
                            size='small'
                            onClick={() => handleEditUser(user)}
                          >
                            <Edit />
                          </IconButton>

                          <IconButton
                            size='small'
                            onClick={() => {
                              setConfirmModalOpen(true);
                              setDeletingUser(user);
                            }}
                          >
                            <DeleteOutline
                              sx={{ color: theme.custom.colors.cancelButton }}
                            />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {loadingMore && (
                    <TableRow>
                      <TableCell colSpan={9} align='center'>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              </TableBody>
            </Table>
            {loading && !loadingMore && (
              <div style={classes.loadingContainer}>
                <CircularProgress />
                <span style={classes.loadingText}>{i18n.t('loading')}</span>
              </div>
            )}
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default Users;
